import { useIntl } from 'react-intl';
import { Form, Input, Select, Row, Col, FormInstance } from 'antd';

import FloatingFormItem from '@/components/_common/FloatingFormItem';

import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
  useDisclosure,
} from '@/hooks';
import {
  BankAccountInfiniteSelectField,
  InputDecimal,
  InputSearch,
  PlanAccountsSelect,
} from '@/components/_common';
import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import { SupplierReduced } from '@/core/domain/people/Suppliers/Suppliers.types';
import { Employee } from '@/core/domain/enterprise/Employees/Employees.types';
import EmployeesSelectedModal from '@/components/_common/EmployeesSelectedModal';
import SuppliersSelectedModal from '@/components/_common/SuppliersSelectedModal';
import {
  Creditor,
  ForecastPayableFormValues,
} from '@/core/domain/financial/ForecastPayable/ForecastPayable.types';
import CompaniesInfiniteSelectField from '@/components/_common/CompaniesInfiniteSelectField';
import DocumentTypesInfiniteSelectField from '@/components/_common/DocumentTypesInfiniteSelectField';
import CostCenterInfiniteSelectField from '@/components/_common/CostCenterInfiniteSelectField';

interface Props {
  form: FormInstance<ForecastPayableFormValues>;
}

function ForecastPayableForm({ form }: Props) {
  const intl = useIntl();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  const [modalEmployeeVisible, modalEmployeeAction] = useDisclosure();
  const [modalSupplierVisible, modalSupplierAction] = useDisclosure();

  const handleSupplierSelect = (supplier?: SupplierReduced) => {
    form.setFieldsValue({
      supplier: supplier?.name,
      supplierId: supplier?.id,
      planAccountId: supplier?.planAccountId,
    });

    modalSupplierAction.close();
  };

  const handleEmployeeSelect = (employee?: Employee) => {
    form.setFieldsValue({
      employee: employee?.name,
      employeeId: employee?.id,
    });

    modalEmployeeAction.close();
  };

  return (
    <>
      <Form layout="vertical" form={form} name="forecastPayableForm">
        <Row gutter={[16, 9]}>
          <Col span={24} md={8} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'predictions.form.company',
              })}
              name="companyId"
              rules={[defaultEmptyFieldError]}
            >
              <CompaniesInfiniteSelectField
                allowClear
                queryFilter={{ active: true }}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={8} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'predictions.form.creditor',
              })}
              name="creditor"
              rules={[defaultEmptyFieldError]}
              initialValue={Creditor.supplier}
            >
              <Select
                style={{ width: '100' }}
                options={[
                  {
                    value: Creditor.supplier,
                    label: intl.formatMessage({
                      id: 'predictions.form.supplier',
                    }),
                  },
                  {
                    value: Creditor.employee,
                    label: intl.formatMessage({
                      id: 'predictions.form.employee',
                    }),
                  },
                ]}
              />
            </FloatingFormItem>
          </Col>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const creditor = getFieldValue('creditor');

              if (creditor === Creditor.employee) {
                return (
                  <Col span={24} md={8} lg={6}>
                    <Form.Item hidden name="employeeId" />

                    <FloatingFormItem
                      name="employee"
                      label={intl.formatMessage({
                        id: 'release.form.employee',
                      })}
                      rules={[defaultEmptyFieldError]}
                    >
                      <InputSearch
                        inputProps={{ disabled: true }}
                        buttonProps={{ onClick: modalEmployeeAction.open }}
                      />
                    </FloatingFormItem>
                  </Col>
                );
              } else {
                return (
                  <Col span={24} md={8} lg={6}>
                    <Form.Item hidden name="supplierId" />

                    <FloatingFormItem
                      name="supplier"
                      label={intl.formatMessage({
                        id: 'release.form.supplier',
                      })}
                      rules={[defaultEmptyFieldError]}
                    >
                      <InputSearch
                        inputProps={{ disabled: true }}
                        buttonProps={{ onClick: modalSupplierAction.open }}
                      />
                    </FloatingFormItem>
                  </Col>
                );
              }
            }}
          </Form.Item>

          <Col span={24} md={8} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'predictions.form.planAccount',
              })}
              name="planAccountId"
              rules={[defaultEmptyFieldError]}
            >
              <PlanAccountsSelect
                allowClear
                queryFilter={{
                  hierarchy: PlanAccountHierarchy.Analitical,
                  destination: PlanAccountDestination.Expense,
                  active: true,
                }}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={16} lg={12}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'predictions.form.description',
              })}
              name="description"
              rules={[defaultEmptyFieldError, defaultMaxLenghtFieldError(250)]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={8} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'predictions.form.documentNumber',
              })}
              name="documentNumber"
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={8} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'predictions.form.documentType',
              })}
              name="documentTypeId"
              rules={[defaultEmptyFieldError]}
            >
              <DocumentTypesInfiniteSelectField
                queryFilter={{ active: true }}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={8} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.costCenter',
              })}
              name="costCenterId"
            >
              <CostCenterInfiniteSelectField queryFilter={{ active: true }} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={8} lg={6}>
            <FloatingFormItem
              name="bankAccountId"
              label={intl.formatMessage({
                id: 'release.form.bankAccount',
              })}
            >
              <BankAccountInfiniteSelectField queryFilter={{ active: true }} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={8} lg={6}>
            <FloatingFormItem
              label="Dia de pagamento"
              name="payday"
              rules={[defaultEmptyFieldError]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={Array.from({ length: 31 }, (_, index) => ({
                  value: index + 1,
                  label: `Dia ${index + 1}`,
                }))}
                onSearch={(value) => {
                  const number = Number(value);

                  if (number >= 1 && number <= 31) {
                    form.setFieldValue('payday', number);
                  }
                }}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={8} lg={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'predictions.form.value',
              })}
              name="value"
              rules={[
                defaultEmptyFieldError,
                {
                  validator: (_, value) => {
                    if (value <= 0) {
                      return Promise.reject(
                        'O valor da parcela deve ser maior que zero!',
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>

      <EmployeesSelectedModal
        visible={modalEmployeeVisible}
        handleVisible={modalEmployeeAction.toggle}
        noSelection
        onEmployeeSelect={handleEmployeeSelect}
      />

      <SuppliersSelectedModal
        visible={modalSupplierVisible}
        handleVisible={modalSupplierAction.toggle}
        noSelection
        onSupplierSelect={handleSupplierSelect}
      />
    </>
  );
}

export default ForecastPayableForm;
